import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material';
import { Search, AccessTime, Person, Description, DriveEta, Badge } from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getLegacyTasks } from '../api/alfresco';
import { getUserInfo } from '../utils/auth';

const TaskList = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fonction pour trier les tâches par date de démarrage (les plus récentes en premier)
  const sortTasksByStartDate = useCallback((tasksArray) => {
    return [...tasksArray].sort((a, b) => {
      const dateA = a.workflowInstance?.startDate ? new Date(a.workflowInstance.startDate) : new Date(0);
      const dateB = b.workflowInstance?.startDate ? new Date(b.workflowInstance.startDate) : new Date(0);
      // Tri décroissant : les plus récentes en premier
      return dateB.getTime() - dateA.getTime();
    });
  }, []);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const userInfo = getUserInfo();
      const username = userInfo?.id || userInfo?.userName;
      const tasksData = await getLegacyTasks({
        authority: username,
        maxItems: 50,
        skipCount: 0,
        exclude: 'wcmwf:*',
        properties: ['bpm_priority', 'bpm_status', 'bpm_dueDate', 'bpm_description','biat_nom','biat_prenom','biat_immatriculation','biat_marque','biat_modele']
      });
      // Trier les tâches par date de démarrage
      const sortedTasks = sortTasksByStartDate(tasksData);
      setTasks(sortedTasks);
      setFilteredTasks(sortedTasks);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors du chargement des tâches'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = tasks;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = tasks.filter((task) => {
        const props = task.properties || {};
        return (
          task.description?.toLowerCase().includes(searchLower) ||
          task.title?.toLowerCase().includes(searchLower) ||
          task.properties?.bpm_description?.toLowerCase().includes(searchLower) ||
          task.workflowInstance?.message?.toLowerCase().includes(searchLower) ||
          props.biat_nom?.toLowerCase().includes(searchLower) ||
          props.biat_prenom?.toLowerCase().includes(searchLower) ||
          props.biat_immatriculation?.toLowerCase().includes(searchLower) ||
          props.biat_marque?.toLowerCase().includes(searchLower) ||
          props.biat_modele?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Trier les tâches filtrées par date de démarrage
    const sortedFiltered = sortTasksByStartDate(filtered);
    setFilteredTasks(sortedFiltered);
  }, [searchTerm, tasks, sortTasksByStartDate]);

  const handleOpenTask = (taskId) => {
    navigate(`/task/${taskId}`);
  };

  const formatDate = (dateString, fallback = '(Aucun)', withTime = false) => {
    if (!dateString) return fallback;
    try {
      const date = new Date(dateString);
      const pattern = withTime ? 'dd/MM/yyyy HH:mm' : 'dd MMMM, yyyy';
      return format(date, pattern, { locale: fr });
    } catch {
      return fallback;
    }
  };

  const getStatusLabel = (task) =>
    task.propertyLabels?.bpm_status ||
    task.properties?.bpm_status ||
    task.state ||
    '—';

const getStatusColor = (status) => {
  if (!status) return 'default';
  const value = status.toLowerCase();
  if (value.includes('termin') || value.includes('complet')) return 'success';
  if (value.includes('prog') || value.includes('cours')) return 'warning';
  if (value.includes('attente')) return 'info';
  return 'default';
};

  const getTypeLabel = (task) => task.title || task.name || '—';

  const getDescription = (task) =>
    task.properties?.bpm_description || task.description || '—';

  const getInitiator = (task) => {
    const initiator = task.workflowInstance?.initiator;
    if (!initiator) return '—';
    const fullName = `${initiator.firstName || ''} ${initiator.lastName || ''}`.trim();
    return fullName || initiator.userName || '—';
  };

  // Fonctions helper pour les informations métier BIAT
  const getClientName = (task) => {
    const props = task.properties || {};
    const nom = props.biat_nom || '';
    const prenom = props.biat_prenom || '';
    const fullName = `${prenom} ${nom}`.trim();
    return fullName || '—';
  };

  const getVehicleInfo = (task) => {
    const props = task.properties || {};
    const immatriculation = props.biat_immatriculation || '';
    const marque = props.biat_marque || '';
    const modele = props.biat_modele || '';
    
    const parts = [];
    if (immatriculation) parts.push(immatriculation);
    if (marque || modele) {
      const vehicule = `${marque} ${modele}`.trim();
      if (vehicule) parts.push(vehicule);
    }
    
    return parts.length > 0 ? parts.join(' - ') : '—';
  };

  const renderTaskDetails = (task) => {
    const statusLabel = getStatusLabel(task);
    const clientName = getClientName(task);
    const vehicleInfo = getVehicleInfo(task);
    const props = task.properties || {};
    
    return (
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: 'primary.main', mb: 0.5 }}>
            {task.title || task.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {task.description || task.properties?.bpm_description || '—'}
          </Typography>
        </Box>
        
        {/* Informations métier BIAT */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1,
          p: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge fontSize="small" color="primary" />
            <Typography variant="body2" color="text.secondary">
              <strong>Client:</strong> {clientName}
            </Typography>
          </Box>
          {vehicleInfo !== '—' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DriveEta fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">
                <strong>Véhicule:</strong> {vehicleInfo}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              <strong>Démarré:</strong> {formatDate(task.workflowInstance?.startDate, '—', true)}
            </Typography>
          </Box>
          {task.properties?.bpm_dueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                <strong>Échéance:</strong> {formatDate(task.properties?.bpm_dueDate, '(Aucun)', true)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              <strong>Par:</strong> {getInitiator(task)}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Chip
            label={statusLabel}
            color={getStatusColor(statusLabel)}
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip
            label={getTypeLabel(task)}
            variant="outlined"
            size="small"
            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
          />
        </Box>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
          Mes tâches
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {filteredTasks.length} tâche{filteredTasks.length > 1 ? 's' : ''} en cours
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par client, véhicule, immatriculation, description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white'
            }
          }}
        />
      </Card>

      {filteredTasks.length === 0 ? (
        <Card sx={{ boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? 'Aucune tâche trouvée' : 'Aucune tâche en cours'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'Essayez de modifier vos critères de recherche' : 'Vous n\'avez actuellement aucune tâche assignée'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredTasks.map((task) => (
            <Grid item xs={12} key={task.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      {renderTaskDetails(task)}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Informations détaillées :
                          </Typography>
                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {task.properties?.biat_immatriculation && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Immatriculation:</strong> {task.properties.biat_immatriculation}
                              </Typography>
                            )}
                            {(task.properties?.biat_marque || task.properties?.biat_modele) && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Marque/Modèle:</strong> {`${task.properties.biat_marque || ''} ${task.properties.biat_modele || ''}`.trim() || '—'}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth={{ xs: true, md: false }}
                          onClick={() => handleOpenTask(task.id)}
                          color="primary"
                        >
                          Ouvrir la tâche
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TaskList;

